import { createContext, ReactElement, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    List,
    NotificationVariant,
    showNotification,
    Stack,
    Text,
    useActiveTenantId,
} from "@flexidao/ui-lib";
import { getFilters, setFilters } from "./utils";
import { setFiltersInUrl } from "./url-utils";
import {
    FiltersContextType,
    FiltersDecoder,
    FiltersProviderProps,
    FiltersValidators,
} from "./types";
import { Page } from "@flexidao/dto";

/**
 * Creates a filters context and a provider for it
 *
 * @template FiltersType - The type of the filters
 *
 * @returns An object with the `useFilters` hook and the `FiltersProvider` component
 */
export const createFiltersContext = <FiltersType extends Record<string, unknown>>({
    page,
    defaultFilters,
    filtersDecoder,
    filtersValidators,
}: {
    page: Page;
    defaultFilters: FiltersType;
    filtersDecoder: FiltersDecoder<FiltersType>;
    filtersValidators?: FiltersValidators<FiltersType>;
}): {
    useFilters: () => FiltersContextType<FiltersType>;
    FiltersProvider: (_: FiltersProviderProps) => ReactElement;
} => {
    const FiltersContext = createContext<FiltersContextType<FiltersType> | null>(null);

    const FiltersProvider = ({ children }: FiltersProviderProps): ReactElement => {
        const [searchParams, setSearchParams] = useSearchParams();
        const tenantId = useActiveTenantId();

        // Get the filters from the URL or local storage and validate them
        const filters: FiltersType = useMemo((): FiltersType => {
            const { filters, urlFilterErrors } = getFilters({
                searchParams,
                defaultFilters,
                filtersDecoder,
                filtersValidators,
                tenantId,
                page,
            });

            // Show notifications for each URL filter error
            urlFilterErrors.forEach(({ key, errors }): void => {
                const title =
                    key === "DECODE_ERROR"
                        ? "Error! Your filters were malformed"
                        : `Error! Invalid value for the ${String(key)} filter`;

                const message =
                    key === "DECODE_ERROR" ? null : errors.length === 1 ? ( //
                        errors[0]
                    ) : (
                        <List>
                            {errors.map((error, index) => (
                                <List.Item key={index}>{error}</List.Item>
                            ))}
                        </List>
                    );

                showNotification({
                    variant: NotificationVariant.Error,
                    title,
                    id: `filter-error-${String(key)}`,
                    message: (
                        <Stack>
                            <Text>Your filters have been reset to the default values.</Text>

                            {message}
                        </Stack>
                    ),
                });
            });

            // If there are errors in the URL, set the correct filters in the URL
            if (urlFilterErrors.length > 0) {
                console.error(
                    "There were some errors in the URL filters. We'll set the correct filters in the URL...",
                    urlFilterErrors,
                );
                setFiltersInUrl({
                    filters,
                    setSearchParams,
                });
            }

            return filters;
        }, [searchParams, setSearchParams, tenantId]);

        // Set the filters in the URL and local storage
        const setFilter: FiltersContextType<FiltersType>["setFilter"] = (key, value): void => {
            const newFilters: FiltersType = {
                ...filters,
                [key]: value,
            };

            setFilters({
                filters: newFilters,
                page,
                tenantId,
                setSearchParams,
            });
        };
        const replaceFilter: FiltersContextType<FiltersType>["replaceFilter"] = (
            key,
            value,
        ): void => {
            const newFilters: FiltersType = {
                ...filters,
                [key]: value,
            };

            setFilters({
                filters: newFilters,
                page,
                tenantId,
                setSearchParams,
                options: {
                    replace: true,
                },
            });
        };

        // This is a hack to be able to set the filters in the URL without reloading the page
        // The `history.replaceState` method will not update the `searchParams`, so the page will not re-render

        // If we used the setSearchParams method, the page would re-render
        // Which would cause the filters to be set again, causing an infinite loop where the user would not be able to go back to the previous page/state
        // This would also cause the famous double flicker effect that we used to have in the app

        // Kudos to https://github.com/remix-run/remix/discussions/7966

        // TODO: FIX THIS! ENABLING IT BREAKS THE BACK BUTTON!
        // eslint-disable-next-line no-restricted-globals
        // history.replaceState(
        //     null,
        //     "",
        //     `?${new URLSearchParams(
        //         parseFiltersToUrlParams({
        //             filters,
        //         }),
        //     ).toString()}`,
        // );

        return (
            <FiltersContext.Provider
                value={{
                    filters,
                    setFilter,
                    replaceFilter,
                }}
            >
                {children}
            </FiltersContext.Provider>
        );
    };

    const useFilters = (): FiltersContextType<FiltersType> => {
        const ctx = useContext(FiltersContext);
        if (!ctx) {
            throw new Error("useFilters must be used within a FiltersProvider");
        }
        return ctx;
    };

    return {
        FiltersProvider,
        useFilters,
    };
};
